// src/Profile.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const API = 'https://snap2excel-backend.onrender.com';

function Profile({ token }) {
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState('');
  const [billingInfo, setBillingInfo] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [planType, setPlanType] = useState('free');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [fullName, setFullName] = useState('');
  const [accountType, setAccountType] = useState('personal');
  const [businessName, setBusinessName] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [role, setRole] = useState('');

  // NEW: how the user uses Snap2Excel (personal vs self-employed)
  const [usageMode, setUsageMode] = useState('personal_budget');

  // NEW: date of birth for youth/adult experience
  const [dateOfBirth, setDateOfBirth] = useState('');

  // which panel is visible: 'details' | 'billing' | 'password' | 'danger' | null
  const [activePanel, setActivePanel] = useState(null);

  const navigate = useNavigate();

  const authAxios = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const loadProfile = async () => {
    if (!token) return;

    try {
      const res = await axios.get(`${API}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProfile(res.data);
      setEmail(res.data.email || '');
      setBillingInfo(res.data.billing_info || '');
      setJobTitle(res.data.job_title || '');
      setPlanType(res.data.plan_type || 'free');

      setFullName(res.data.full_name || '');
      setAccountType(res.data.account_type || 'personal');
      setBusinessName(res.data.business_name || '');
      setCountry(res.data.country || '');
      setCity(res.data.city || '');
      setRole(res.data.role || '');

      setUsageMode(res.data.usage_mode || 'personal_budget');
      setDateOfBirth(res.data.date_of_birth || '');
    } catch (err) {
      console.error('Profile load error', err);
      setMessage('Failed to load profile');
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // FIX: profile_photo is stored as "profile_uploads/..." and served at "/profile-uploads"
  const avatarUrl =
    profile && profile.profile_photo
      ? `${API}/${profile.profile_photo.replace(/^profile_uploads/, 'profile-uploads')}`
      : null;

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleEmailSave = async () => {
    if (!email) return;
    setSaving(true);
    try {
      const res = await authAxios.post('/api/profile/email', { email });
      setProfile((p) => ({ ...p, email: res.data.email }));
      showMessage('Email updated');
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.error || 'Failed to update email');
    }
    setSaving(false);
  };

  const handleBillingSave = async () => {
    setSaving(true);
    try {
      const res = await authAxios.post('/api/profile/billing', {
        billing_info: billingInfo,
      });
      setProfile((p) => ({ ...p, billing_info: res.data.billing_info }));
      showMessage('Billing notes saved');
    } catch (err) {
      console.error(err);
      showMessage('Failed to save billing info');
    }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword) {
      showMessage('Fill both password fields');
      return;
    }
    setSaving(true);
    try {
      await authAxios.post('/api/profile/password', {
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      showMessage('Password updated');
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.error || 'Failed to change password');
    }
    setSaving(false);
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) return;
    setSaving(true);
    const fd = new FormData();
    fd.append('photo', photoFile);

    try {
      const res = await authAxios.post('/api/profile/photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile((p) => ({
        ...p,
        profile_photo: res.data.profile_photo,
      }));
      setPhotoFile(null);
      showMessage('Profile photo updated');
    } catch (err) {
      console.error(err);
      showMessage('Failed to upload photo');
    }
    setSaving(false);
  };

  const handleDetailsSave = async () => {
    setSaving(true);
    try {
      await authAxios.post('/api/profile/details', {
        full_name: fullName,
        account_type: accountType,
        business_name: accountType === 'business' ? businessName : '',
        country,
        city,
        role,
        usage_mode: usageMode,
        date_of_birth: dateOfBirth || null,
      });

      await loadProfile();
      showMessage('Profile details saved');
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.error || 'Failed to save profile details');
    }
    setSaving(false);
  };

  const handleJobSave = async () => {
    setSaving(true);
    try {
      await authAxios.post('/api/profile/job', {
        job_title: jobTitle,
        plan_type: planType, // backend currently ignores this, but harmless
      });

      await loadProfile();
      showMessage('Job & plan saved (local flag only)');
    } catch (err) {
      console.error(err);
      showMessage('Failed to save job/plan');
    }
    setSaving(false);
  };

  // Stripe upgrade – picks the right Stripe plan code based on account type
  const handleUpgradeToPro = async () => {
    setSaving(true);
    try {
      const plan_type = accountType === 'business' ? 'bw_149' : 'pw_099';
      const res = await authAxios.post('/api/billing/create-checkout-session', {
        plan_type,
      });
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      } else {
        showMessage('Could not start checkout');
      }
    } catch (err) {
      console.error(err);
      showMessage(
        err.response?.data?.error || 'Failed to start Stripe checkout'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      showMessage('Enter your password to delete');
      return;
    }
    if (
      !window.confirm(
        'Are you sure? This will delete your account and all receipts.'
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await authAxios.delete('/api/profile', {
        data: { password: deletePassword },
      });
      localStorage.removeItem('token');
      navigate('/register');
    } catch (err) {
      console.error(err);
      showMessage(err.response?.data?.error || 'Failed to delete account');
    }
    setSaving(false);
  };

  const handleBack = () => {
    navigate('/app');
  };

  function readablePlan(type) {
    return (
      {
        free: 'Free (beta)',
        weekly_099: 'Personal Pro – £0.99/week',
        weekly_149: 'Business Pro – £1.49/week',
        pw_099: 'Personal Pro – £0.99/week',
        bw_149: 'Business Pro – £1.49/week',
      }[type] || 'Unknown'
    );
  }

  const proTitle =
    accountType === 'business'
      ? 'Snap2Excel Business Pro – £1.49/week'
      : 'Snap2Excel Personal Pro – £0.99/week';

  const openPanel = (panel) => {
    // toggle off if same panel
    const next = panel === activePanel ? null : panel;
    setActivePanel(next);

    // scroll to the panel when opening
    if (next) {
      setTimeout(() => {
        const el = document.getElementById('profile-panel');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 0);
    }
  };

  const experienceLabel =
    profile?.experience_mode === 'youth'
      ? 'Youth (simplified interface)'
      : 'Adult (full interface)';

  return (
    <div className="app-root">
      <div className="app-container">
        <header className="app-header">
          <div>
            <h1 className="app-title">Profile</h1>
            <p className="app-subtitle">
              Manage your Snap2Excel account, plan, and billing details.
            </p>
          </div>
          <div>
            <button className="btn btn-secondary" onClick={handleBack}>
              ← Back to app
            </button>
          </div>
        </header>

        {message && <div className="profile-message">{message}</div>}

        {profile && (
          <>
            {/* SUMMARY CARD – always visible */}
            <section className="profile-card">
              <h2>Account & plan</h2>

              <div className="profile-avatar-row">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="avatar" className="profile-avatar" />
                ) : (
                  <div className="profile-avatar placeholder">
                    {profile.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}

                <div>
                  <div className="profile-email" style={{ fontWeight: 600 }}>
                    {fullName || profile.email}
                  </div>
                  <div
                    className="profile-email"
                    style={{ fontSize: 12, opacity: 0.8 }}
                  >
                    {profile.email}
                  </div>
                  <div className="profile-plan">
                    Current plan: <strong>{readablePlan(planType)}</strong>
                  </div>
                  <div className="profile-plan">
                    Account type:{' '}
                    <strong>
                      {accountType === 'business' ? 'Business' : 'Personal'}
                    </strong>
                  </div>

                  <div className="profile-plan">
                    Experience mode:{' '}
                    <strong>{experienceLabel}</strong>
                  </div>

                  {businessName && accountType === 'business' && (
                    <div className="profile-plan">
                      Business name: <strong>{businessName}</strong>
                    </div>
                  )}

                  {(country || city) && (
                    <div className="profile-plan" style={{ fontSize: 12 }}>
                      Location: {city && `${city}, `}
                      {country}
                    </div>
                  )}

                  {role && (
                    <div className="profile-plan" style={{ fontSize: 12 }}>
                      Role: {role}
                    </div>
                  )}

                  {dateOfBirth && (
                    <div className="profile-plan" style={{ fontSize: 12 }}>
                      Date of birth: {dateOfBirth}
                    </div>
                  )}

                  {planType !== 'free' && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: '#16a34a',
                        fontWeight: 500,
                      }}
                    >
                      PRO active (billing to be connected)
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* ACTION BUTTONS – choose what to edit */}
            <div
              className="profile-card"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, marginRight: 8 }}>
                What do you want to change?
              </span>
              <button
                type="button"
                className={
                  'btn btn-secondary' +
                  (activePanel === 'details' ? ' profile-tab-active' : '')
                }
                onClick={() => openPanel('details')}
              >
                Profile & plan
              </button>
              <button
                type="button"
                className={
                  'btn btn-secondary' +
                  (activePanel === 'billing' ? ' profile-tab-active' : '')
                }
                onClick={() => openPanel('billing')}
              >
                Billing notes
              </button>
              <button
                type="button"
                className={
                  'btn btn-secondary' +
                  (activePanel === 'password' ? ' profile-tab-active' : '')
                }
                onClick={() => openPanel('password')}
              >
                Password
              </button>
              <button
                type="button"
                className={
                  'btn btn-secondary' +
                  (activePanel === 'danger' ? ' profile-tab-active' : '')
                }
                style={{ marginLeft: 'auto' }}
                onClick={() => openPanel('danger')}
              >
                Danger zone
              </button>
            </div>

            {/* PANEL AREA – only one visible at a time */}
            <div id="profile-panel" className="profile-grid">
              {activePanel === 'details' && (
                <section className="profile-card">
                  <h2>Edit profile & plan</h2>

                  <div className="profile-field">
                    <label>Full name</label>
                    <input
                      className="auth-input"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="profile-field">
                    <label>Date of birth</label>
                    <input
                      className="auth-input"
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                    />
                    <p className="profile-help">
                      We use this to give under-25s a simpler money view.
                    </p>
                  </div>

                  <div className="profile-field">
                    <label>Account type</label>
                    <select
                      className="auth-input"
                      value={accountType}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setAccountType(nextType);

                        // if switching to business, default plan to business Pro
                        if (nextType === 'business' && planType === 'weekly_099') {
                          setPlanType('weekly_149');
                        }
                      }}
                    >
                      <option value="personal">Personal</option>
                      <option value="business">Business</option>
                    </select>
                  </div>

                  {accountType === 'business' && (
                    <div className="profile-field">
                      <label>Business name</label>
                      <input
                        className="auth-input"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        placeholder="Company name"
                      />
                    </div>
                  )}

                  {/* Usage mode: only relevant for personal accounts */}
                  {accountType === 'personal' && (
                    <div className="profile-field">
                      <label>How are you using Snap2Excel?</label>
                      <select
                        className="auth-input"
                        value={usageMode}
                        onChange={(e) => setUsageMode(e.target.value)}
                      >
                        <option value="personal_budget">
                          Just for my personal budget
                        </option>
                        <option value="self_employed">
                          I’m self-employed / side hustle
                        </option>
                      </select>
                    </div>
                  )}

                  <div className="profile-field">
                    <label>Country</label>
                    <input
                      className="auth-input"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="e.g. United Kingdom"
                    />
                  </div>

                  <div className="profile-field">
                    <label>City</label>
                    <input
                      className="auth-input"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="e.g. Margate"
                    />
                  </div>

                  <div className="profile-field">
                    <label>Your role</label>
                    <input
                      className="auth-input"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      placeholder="e.g. Chef, Accountant, Self-employed"
                    />
                  </div>

                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={handleDetailsSave}
                    disabled={saving}
                    style={{ marginBottom: 16 }}
                  >
                    Save profile details
                  </button>

                  {/* Job + plan */}
                  <div className="profile-field">
                    <label>Your job / role (for analytics)</label>
                    <input
                      className="auth-input"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="e.g. Chef, Accountant, Self-employed"
                    />
                  </div>

                  <div className="profile-field">
                    <label>Plan (stored in your account)</label>
                    <select
                      className="auth-input"
                      value={planType}
                      onChange={(e) => setPlanType(e.target.value)}
                    >
                      <option value="free">Free (beta)</option>
                      <option value="weekly_099">
                        Personal Pro – £0.99/week
                      </option>
                      <option value="weekly_149">
                        Business Pro – £1.49/week
                      </option>
                    </select>
                    <p className="profile-help" style={{ marginTop: 6 }}>
                      {planType === 'free' && (
                        <>
                          <strong>Free:</strong> Up to 50 receipts, basic
                          dashboard. Perfect to try Snap2Excel.
                        </>
                      )}
                      {planType === 'weekly_099' && (
                        <>
                          <strong>Personal Pro (£0.99/week):</strong> Unlimited
                          receipts and exports, ideal for self-employed and side
                          hustles.
                        </>
                      )}
                      {planType === 'weekly_149' && (
                        <>
                          <strong>Business Pro (£1.49/week):</strong> Unlimited
                          receipts, exports and reports for companies and VAT
                          bookkeeping.
                        </>
                      )}
                    </p>

                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handleJobSave}
                      disabled={saving}
                      style={{ marginTop: 6 }}
                    >
                      Save job & plan
                    </button>
                    <p className="profile-help">
                      This marks your account as Free or Pro inside Snap2Excel.
                      Stripe billing connection is wired separately.
                    </p>
                  </div>

                  {/* Pro upsell card */}
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 14,
                      background:
                        'linear-gradient(135deg, #0ea5e9, #22c55e)',
                      color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                      {proTitle}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>
                      Unlimited receipt scans, PDF reports, and all dashboard
                      features.
                    </div>
                    <button
                      type="button"
                      className="btn"
                      style={{
                        alignSelf: 'flex-start',
                        background: 'white',
                        color: '#0f172a',
                        padding: '6px 14px',
                        fontSize: 12,
                        marginTop: 4,
                      }}
                      onClick={handleUpgradeToPro}
                      disabled={saving}
                    >
                      Upgrade to Pro
                    </button>
                  </div>

                  {/* Email + photo */}
                  <div
                    className="profile-field"
                    style={{ marginTop: 16 }}
                  >
                    <label>Change email</label>
                    <input
                      className="auth-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                    />
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={handleEmailSave}
                      disabled={saving}
                    >
                      Save email
                    </button>
                  </div>

                  <div className="profile-field">
                    <label>Profile photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setPhotoFile(e.target.files[0])
                      }
                    />
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={handlePhotoUpload}
                      disabled={saving || !photoFile}
                      style={{ marginTop: 8 }}
                    >
                      Upload photo
                    </button>
                  </div>
                </section>
              )}

              {activePanel === 'billing' && (
                <section className="profile-card">
                  <h2>Billing info / notes</h2>
                  <p className="profile-help">
                    Optional: company name, VAT number, notes for invoices or
                    accountants.
                  </p>
                  <textarea
                    className="auth-textarea"
                    rows={6}
                    value={billingInfo}
                    onChange={(e) => setBillingInfo(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleBillingSave}
                    disabled={saving}
                    style={{ marginTop: 8 }}
                  >
                    Save billing info
                  </button>
                </section>
              )}

              {activePanel === 'password' && (
                <section className="profile-card">
                  <h2>Change password</h2>
                  <div className="profile-field">
                    <label>Current password</label>
                    <input
                      className="auth-input"
                      type="password"
                      value={currentPassword}
                      onChange={(e) =>
                        setCurrentPassword(e.target.value)
                      }
                    />
                  </div>
                  <div className="profile-field">
                    <label>New password</label>
                    <input
                      className="auth-input"
                      type="password"
                      value={newPassword}
                      onChange={(e) =>
                        setNewPassword(e.target.value)
                      }
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={saving}
                  >
                    Update password
                  </button>
                </section>
              )}

              {activePanel === 'danger' && (
                <section className="profile-card danger">
                  <h2>Danger zone</h2>
                  <p className="profile-help">
                    Delete your account and all receipts. This cannot be undone.
                  </p>
                  <div className="profile-field">
                    <label>Confirm password</label>
                    <input
                      className="auth-input"
                      type="password"
                      value={deletePassword}
                      onChange={(e) =>
                        setDeletePassword(e.target.value)
                      }
                    />
                  </div>
                  <button
                    className="btn"
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={saving}
                    style={{
                      background: '#dc2626',
                      color: 'white',
                    }}
                  >
                    Delete my account
                  </button>
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Profile;
